if (!customElements.get('free-gift')) {

  class FreeGift extends HTMLElement {

    constructor() {
      super();

      this.options = {
        max_gifts: 1,
        max_products: 2,
        gift_threshold: 0,
        translations: {
          header: 'Add a free gift',
          header_added: 'Free gift added:'
        }
      };

      if (this?.dataset?.options) {
        const dataOptions = parseJson(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions
        };
      }

      this.discountTitle = FreeGift.DEFAULT_DISCOUNT_TITLE;
      this.giftAdjustment = null;
      this.isUpdating = false;
      this.lastSelectedGiftVariantId = null;

      FreeGift.bindListeners();
    }

    connectedCallback() {
      if (FreeGift.isMutatingCart) {
        return;
      }

      this.initializeCartState({
        waitForDiscountKit: !FreeGift.lastAdjustment
      });
    }

    static bindListeners() {
      if (FreeGift.listenersBound) {
        return;
      }

      document.addEventListener('discount_kit:gift_adjustments', (event) => {
        FreeGift.handleDiscountKitUpdate(event.detail);
      });

      document.addEventListener('discount_kit:cart_changed', () => {
        FreeGift.handleDiscountKitUpdate(null);
      });

      FreeGift.listenersBound = true;
    }

    static handleDiscountKitUpdate(payload) {
      if (FreeGift.isMutatingCart) {
        return;
      }

      // Save immediately — before the debounce — so a subsequent cart_changed (null)
      // event can't race and cause the debounce to run without this data.
      if (payload?.gift_products?.length || payload?.add?.options?.length) {
        FreeGift.lastDiscountKitPayload = payload;
      }

      clearTimeout(FreeGift.debounceTimer);

      FreeGift.debounceTimer = setTimeout(() => {
        const state = FreeGift.lastDiscountKitPayload || FreeGift.resolveDiscountKitState();
        const normalized = FreeGift.normalizePayload(state);

        if (normalized) {
          FreeGift.lastAdjustment = normalized;
        }

        document.querySelectorAll('free-gift').forEach((freeGift) => {
          if (normalized) {
            freeGift.initializeCartState({ waitForDiscountKit: false });
          } else {
            freeGift.refreshUIFromCart();
          }
        });
      }, 150);
    }

    /**
     * Theme-rendered catalog when Discount Kit globals are unavailable.
     */
    static loadCatalogFromDOM() {
      const element = document.getElementById('free-gift-catalog');

      if (!element?.textContent?.trim()) {
        return null;
      }

      try {
        const gift_products = parseJson(element.textContent);

        if (!gift_products?.length) {
          return null;
        }

        return { gift_products };
      } catch (error) {
        console.warn('free-gift: invalid catalog JSON', error);
        return null;
      }
    }

    /**
     * Read Discount Kit runtime state from known globals (app embed).
     */
    static getMaxProducts() {
      const element = document.querySelector('free-gift');

      return element?.options?.max_products || 2;
    }

    static resolveDiscountKitState() {
      const candidates = [
        FreeGift.lastDiscountKitPayload,
        FreeGift.loadCatalogFromDOM(),
        typeof window !== 'undefined' ? window.discount_kit : null,
        typeof window !== 'undefined' ? window.DiscountKit : null,
        typeof window !== 'undefined' ? window.discountKit : null,
        typeof window !== 'undefined' ? window.Shopify?.discount_kit : null,
        typeof window !== 'undefined' ? window.Shopify?.apps?.discount_kit : null
      ].filter(Boolean);

      for (const candidate of candidates) {
        if (candidate?.gift_products?.length) {
          return candidate;
        }

        if (candidate?.add?.options?.length) {
          return candidate;
        }

        if (candidate?.config?.gift_products?.length) {
          return {
            ...candidate,
            gift_products: candidate.config.gift_products,
            config: candidate.config
          };
        }

        if (candidate?.core?.config && candidate?.gift_products) {
          return candidate;
        }
      }

      if (typeof window !== 'undefined') {
        for (const key of Object.keys(window)) {
          if (!/discount/i.test(key)) {
            continue;
          }

          const candidate = window[key];

          if (candidate?.gift_products?.length) {
            return candidate;
          }
        }
      }

      return null;
    }

    /**
     * Normalize Discount Kit doc event format OR runtime gift_products format.
     * @param {object} payload
     * @returns {object|null}
     */
    static normalizePayload(payload) {
      if (!payload) {
        return null;
      }

      if (payload.add?.options?.length) {
        return payload;
      }

      const giftProducts = payload.gift_products || payload.config?.gift_products;

      if (!giftProducts?.length) {
        return null;
      }

      const maxProducts = FreeGift.getMaxProducts();
      const options = [];

      for (let i = 0; i < giftProducts.length && options.length < maxProducts; i++) {
        const entry = giftProducts[i];
        const product = entry.product || entry;
        const variants = FreeGift.getFreeVariantsFromProduct(product);

        if (!variants.length) {
          continue;
        }

        options.push({
          id: String(product.id),
          title: product.title,
          image: FreeGift.normalizeImageUrl(product.featured_image || product.images?.[0]),
          variants
        });
      }

      if (!options.length) {
        return null;
      }

      const cart = payload.config?.cart || payload.core?.config?.cart || payload.core?._cart;
      const discountTitle = FreeGift.getDiscountTitle(payload);
      const eligibleVariantIds = options.flatMap((option) => option.variants.map((variant) => Number(variant.id)));

      return {
        add: {
          discountId: FreeGift.getDiscountId(payload),
          discountTitle,
          quantity: 1,
          options
        },
        remove: FreeGift.buildRemoveMapFromCart(cart, eligibleVariantIds)
      };
    }

    static getDiscountTitle(payload) {
      const discounts = payload.config?.discounts
        || payload.core?._discounts
        || payload.core?.config?.discounts
        || payload.discounts
        || [];

      const discount = discounts[0];

      if (discount?.discountTitle) {
        return discount.discountTitle;
      }

      if (discount?.title) {
        return discount.title;
      }

      return FreeGift.DEFAULT_DISCOUNT_TITLE;
    }

    static getDiscountId(payload) {
      const discounts = payload.config?.discounts || payload.discounts || [];

      return discounts[0]?.discountId || discounts[0]?.id || '';
    }

    static getFreeVariantsFromProduct(product) {
      if (!product?.variants?.length) {
        return [];
      }

      return product.variants
        .filter((variant) => FreeGift.isFreeGiftVariant(variant))
        .filter((variant) => variant.available !== false)
        .map((variant) => ({
          id: variant.id,
          title: variant.title,
          available: true,
          price: variant.price,
          image: FreeGift.normalizeImageUrl(
            variant.featured_image?.src
            || variant.featured_image?.url
            || variant.image
            || product.featured_image
            || product.images?.[0]
          )
        }));
    }

    static isFreeGiftVariant(variant) {
      const title = (variant.title || variant.public_title || '').toLowerCase();

      return title.includes('free');
    }

    static normalizeImageUrl(url) {
      if (!url) {
        return '';
      }

      if (typeof url === 'object') {
        return FreeGift.normalizeImageUrl(url.src || url.url);
      }

      if (url.startsWith('//')) {
        return `https:${url}`;
      }

      return url;
    }

    static buildRemoveMapFromCart(cart, eligibleVariantIds = []) {
      const remove = {};
      const items = cart?.items || [];

      items.forEach((item) => {
        const properties = item.properties || {};
        const isGift = properties._dk_gift != null && properties._dk_gift !== '';

        if (!isGift) {
          return;
        }

        const variantId = Number(item.variant_id || item.id);

        if (!eligibleVariantIds.length || !eligibleVariantIds.includes(variantId)) {
          remove[item.key] = 0;
        }
      });

      return remove;
    }

    async initializeCartState({ waitForDiscountKit = true } = {}) {
      if (FreeGift.isMutatingCart) {
        return;
      }

      let cart = await this.fetchCart();

      this.syncProgressBars(cart);

      // Always enforce: remove gifts below threshold, clamp qty to 1, remove excess gifts.
      const cleaned = await this.cleanupGiftsIfNeeded(cart);
      if (cleaned) {
        cart = await this.fetchCart();
        this.syncProgressBars(cart);
      }

      if (!this.isEligibleForGift(cart)) {
        console.info('[free-gift] not eligible — threshold:', this.options.gift_threshold, 'cart total:', cart.total_price);
        this.hide();
        return;
      }

      let normalized = FreeGift.normalizePayload(FreeGift.lastAdjustment) || FreeGift.lastAdjustment;

      if (!normalized?.add?.options?.length && waitForDiscountKit) {
        console.info('[free-gift] waiting for Discount Kit state…');
        normalized = await this.waitForDiscountKitState();
      }

      if (!normalized?.add?.options?.length) {
        console.warn('[free-gift] no gift data — set "Gift collection (fallback)" in block settings, or connect Discount Kit. DOM catalog:', FreeGift.loadCatalogFromDOM());
        this.hide();
        return;
      }

      await this.applyGiftAdjustment(normalized);
    }

    async resolveGiftState({ waitForDiscountKit = false } = {}) {
      let normalized = FreeGift.normalizePayload(FreeGift.lastAdjustment) || FreeGift.lastAdjustment;

      if (!normalized?.add?.options?.length) {
        normalized = FreeGift.normalizePayload(FreeGift.resolveDiscountKitState());
      }

      if (!normalized?.add?.options?.length && waitForDiscountKit) {
        normalized = await this.waitForDiscountKitState();
      }

      if (normalized?.add?.options?.length) {
        FreeGift.lastAdjustment = normalized;
      }

      return normalized;
    }

    syncProgressBarsSafely(cart) {
      try {
        this.syncProgressBars(cart);
      } catch (error) {
        console.warn('[free-gift] progress bar sync failed', error);
      }
    }

    /**
     * Update UI only — no cart API calls (used after theme cart quantity changes).
     */
    async refreshUIFromCart(cartState) {
      if (FreeGift.isMutatingCart) {
        return;
      }

      let cart = cartState?.items ? cartState : await this.fetchCart();

      this.syncProgressBarsSafely(cart);

      const cleaned = await this.cleanupGiftsIfNeeded(cart);
      if (cleaned) {
        cart = await this.fetchCart();
        this.syncProgressBarsSafely(cart);
      }

      if (!this.isEligibleForGift(cart)) {
        this.hide();
        return;
      }

      const normalized = await this.resolveGiftState();

      if (!normalized?.add?.options?.length) {
        this.hide();
        return;
      }

      this.giftAdjustment = normalized;
      this.discountTitle = normalized.add.discountTitle || FreeGift.DEFAULT_DISCOUNT_TITLE;

      const availableVariants = this.getAvailableVariants();

      if (!availableVariants.length) {
        this.hide();
        return;
      }

      const currentSelected = this.getSelectedGiftVariantId(cart);
      if (this.lastSelectedGiftVariantId && !currentSelected) {
        sessionStorage.setItem('gift_manually_removed', true);
      }

      this.renderGiftList(availableVariants);
      this.show();
      await this.syncSelectionFromCart(cart);
    }

    async waitForDiscountKitState(attempts = 15, delayMs = 200) {
      for (let attempt = 0; attempt < attempts; attempt++) {
        const state = FreeGift.resolveDiscountKitState();
        const normalized = FreeGift.normalizePayload(state);

        if (normalized?.add?.options?.length) {
          FreeGift.lastAdjustment = normalized;
          return normalized;
        }

        if (attempt < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      return null;
    }

    static parseCartJson(response) {
      return response.json().then((data) => {
        if (data?.status && Number(data.status) >= 400) {
          throw new Error(data.description || data.message || 'Cart update failed');
        }

        return data;
      });
    }

    fetchCart() {
      return fetch(`${routes.cart_url}.js`).then((response) => response.json());
    }

    isEligibleForGift(cart) {
      const threshold = Number(this.options.gift_threshold) || 0;

      if (threshold <= 0) {
        return true;
      }

      return cart.total_price >= threshold;
    }

    /**
     * Replicates the Shopify Script enforcement:
     * - Remove gifts if below threshold or cart has only gifts
     * - Remove excess gifts beyond max_gifts
     * - Reduce quantity to 1 if somehow > 1
     * Returns true if any cart mutation was made.
     */
    async cleanupGiftsIfNeeded(cart) {
      const giftItems = this.getGiftLineItems(cart);

      if (!giftItems.length) {
        return false;
      }

      const regularItems = cart.items.filter((item) => !this.isGiftLineItem(item));
      const hasOnlyGifts = regularItems.length === 0;
      const belowThreshold = !this.isEligibleForGift(cart);
      const maxGifts = Number(this.options.max_gifts) || 1;

      const updates = {};

      if (hasOnlyGifts || belowThreshold) {
        giftItems.forEach((item) => { updates[item.key] = 0; });
      } else {
        giftItems.forEach((item, index) => {
          if (index >= maxGifts) {
            updates[item.key] = 0;
          } else if (item.quantity > 1) {
            updates[item.key] = 1;
          }
        });
      }

      if (!Object.keys(updates).length) {
        return false;
      }

      FreeGift.isMutatingCart = true;

      try {
        const body = JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: window.location.pathname
        });

        const updatedCart = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body
        }).then(FreeGift.parseCartJson);

        this.refreshCartSections(updatedCart.sections);
        this.syncProgressBars(updatedCart);
      } finally {
        FreeGift.isMutatingCart = false;
      }

      return true;
    }

    syncProgressBars(cart) {
      const root = this.closest('.cart-drawer__block, .cart__progress-block, .cart__extra') || document;

      if (typeof window.updateProgressBars === 'function') {
        window.updateProgressBars(cart, root);
      }
    }

    get content() {
      return this.querySelector('[data-free-gift-content]');
    }

    get list() {
      return this.querySelector('[data-free-gift-list]');
    }

    /**
     * Apply normalized Discount Kit gift adjustment payload.
     * @param {object} detail
     */
    async applyGiftAdjustment(detail) {
      if (FreeGift.isMutatingCart) {
        return;
      }

      const normalized = FreeGift.normalizePayload(detail) || detail;

      if (!normalized?.add?.options?.length) {
        this.hide();
        return;
      }

      this.giftAdjustment = normalized;
      FreeGift.lastAdjustment = normalized;
      this.discountTitle = normalized.add.discountTitle || FreeGift.DEFAULT_DISCOUNT_TITLE;

      const cart = await this.fetchCart();

      const availableVariants = this.getAvailableVariants();

      if (!availableVariants.length) {
        this.hide();
        return;
      }

      this.renderGiftList(availableVariants);
      this.show();
      await this.syncSelectionFromCart(cart);
    }

    async update(state) {
      await this.refreshUIFromCart(state);
    }

    getAvailableVariants() {
      const options = this.giftAdjustment?.add?.options;

      if (!options?.length) {
        return [];
      }

      const maxProducts = this.options.max_products || 2;
      const variants = [];
      const seenVariantIds = new Set();

      for (let i = 0; i < options.length && i < maxProducts; i++) {
        const option = options[i];

        for (const variant of option.variants || []) {
          const variantId = String(variant.id);

          if (variant.available === false || seenVariantIds.has(variantId)) {
            continue;
          }

          seenVariantIds.add(variantId);
          variants.push({
            ...variant,
            productTitle: option.title,
            productImage: variant.image || option.image
          });
        }
      }

      return variants;
    }

    isGiftLineItem(item) {
      return item?.properties?._dk_gift != null && item.properties._dk_gift !== '';
    }

    getGiftLineItems(cart) {
      return cart.items.filter((item) => this.isGiftLineItem(item));
    }

    getSelectedGiftVariantId(cart) {
      const eligibleIds = this.getAvailableVariants().map((variant) => Number(variant.id));

      const giftItem = cart.items.find((item) => {
        const variantId = Number(item.variant_id);

        if (eligibleIds.includes(variantId)) {
          return true;
        }

        return this.isGiftLineItem(item);
      });

      return giftItem ? String(giftItem.variant_id) : null;
    }

    async syncSelectionFromCart(cartState) {
      let cart = cartState;

      if (!cart?.items) {
        cart = await this.fetchCart();
      }

      const selectedVariantId = this.getSelectedGiftVariantId(cart);

      this.querySelectorAll('[data-free-gift-item]').forEach((input) => {
        input.checked = input.getAttribute('data-free-gift-item') === selectedVariantId;
      });

      this.updateHeaderState(Boolean(selectedVariantId));
      this.lastSelectedGiftVariantId = selectedVariantId;
    }

    isGiftManuallyRemoved() {
      return sessionStorage.getItem('gift_manually_removed') === 'true';
    }

    updateHeaderState(hasSelection) {
      const icon = this.querySelector('[data-free-gift-header-icon]');
      const text = this.querySelector('[data-free-gift-header-text]');
      const translations = this.options.translations || {};

      if (hasSelection) {
        this.classList.add('free-gift--has-selection');
        icon?.removeAttribute('hidden');

        if (text) {
          text.textContent = translations.header_added || 'Free gift added:';
        }
      } else {
        this.classList.remove('free-gift--has-selection');
        icon?.setAttribute('hidden', '');

        if (text) {
          text.textContent = translations.header || 'Add a free gift';
        }
      }
    }

    show() {
      if (!this.content) {
        return;
      }

      this.style.height = `${this.content.offsetHeight}px`;
      this.classList.remove('free-gift--hidden');
    }

    hide() {
      this.style.height = '0px';
      this.classList.add('free-gift--hidden');
    }

    renderGiftList(variants) {
      const template = this.querySelector('[data-free-gift-item-template]');

      if (!this.list || !template) {
        return;
      }

      const currentIds = [...this.list.querySelectorAll('[data-free-gift-item]')]
        .map((el) => el.getAttribute('data-free-gift-item')).join(',');
      const newIds = variants.map((v) => String(v.id)).join(',');

      if (currentIds === newIds && currentIds !== '') {
        return;
      }

      this.list.innerHTML = '';

      const giftManuallyRemoved = this.isGiftManuallyRemoved();

      variants.forEach((variant) => {
        const variantId = String(variant.id);
        const variantTitle = (variant.title || '').split('|').pop().trim();
        const imageSrc = variant.productImage || '';
        const item = template.content.cloneNode(true);
        const label = item.querySelector('[data-free-gift-label]');
        const input = item.querySelector('[data-free-gift-item]');
        const imageWrapper = item.querySelector('[data-free-gift-image]');
        const title = item.querySelector('[data-free-gift-title]');
        const variantTitleEl = item.querySelector('[data-free-gift-variant-title]');

        label.setAttribute('for', `free-gift-item-${variantId}`);
        input.id = `free-gift-item-${variantId}`;
        input.setAttribute('data-free-gift-item', variantId);

        if (variants.length === 1 && !giftManuallyRemoved) {
          this.updateGift(variantId, true);
        }

        if (title) {
          title.textContent = variant.productTitle || '';
        }

        if (variantTitleEl) {
          variantTitleEl.textContent = variantTitle;
        }

        if (imageWrapper && imageSrc) {
          const imageAlt = this.escapeHtml(variant.productTitle || '');
          imageWrapper.innerHTML = `<figure class="img img--wrapper img--cover free-gift__item-image img--4x5"><picture><img loading="lazy" src="${this.escapeHtml(imageSrc)}" alt="${imageAlt}" width="40" height="50"></picture></figure>`;
        }

        this.list.appendChild(item);
      });
    }

    escapeHtml(string) {
      return String(string)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    getSectionsToRender({ includeCartDrawer = false } = {}) {
      const sections = [];
      const mainCartItems = document.getElementById('main-cart-items');

      if (mainCartItems?.dataset?.id) {
        sections.push(
          {
            id: 'main-cart-items',
            section: mainCartItems.dataset.id,
            selector: '.js-cart-item-contents',
          },
          {
            id: 'main-cart-items',
            section: mainCartItems.dataset.id,
            selector: '.js-cart-bubble-contents',
          }
        );
      }

      const mainCartFooter = document.getElementById('main-cart-footer');

      if (mainCartFooter?.dataset?.id) {
        sections.push({
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-cart-footer-contents',
        });
      }

      if (includeCartDrawer && document.getElementById('cart-drawer')) {
        sections.push({
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: '[data-cart-drawer]',
        });
      }

      if (document.getElementById('cart-icon-bubble')) {
        sections.push({
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        });
      }

      return sections;
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    static async fetchCartSections({ includeCartDrawer = false } = {}) {
      const instance = document.querySelector('free-gift');

      if (!instance) {
        return null;
      }

      const sectionsToRender = instance.getSectionsToRender({ includeCartDrawer });

      if (!sectionsToRender.length) {
        return null;
      }

      const sectionParam = sectionsToRender.map((section) => section.section).join(',');
      const response = await fetch(`${routes.cart_url}?sections=${sectionParam}`, {
        headers: { Accept: 'application/json' }
      });

      return response.json();
    }

    refreshCartSections(sections) {
      if (!sections) {
        return;
      }

      this.getSectionsToRender({ includeCartDrawer: false }).forEach((section) => {
        const sectionHtml = sections[section.section];

        if (!sectionHtml) {
          return;
        }

        const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector)
          || document.getElementById(section.id);

        if (elementToReplace) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      });

      document.querySelector('cart-drawer')?.handleScrollHeight();
    }

    updateGift(id, add = true) {
      if (!id && add === true) {
        return Promise.resolve(false);
      }

      if (!this.discountTitle) {
        this.discountTitle = FreeGift.DEFAULT_DISCOUNT_TITLE;
      }

      return new Promise((resolve, reject) => {
        (async () => {
          try {
            if (this.isUpdating) {
              resolve();
              return;
            }

            this.isUpdating = true;
            FreeGift.isMutatingCart = true;

            const cartItems = document.querySelector('cart-items');
            cartItems?.enableLoading();

            let sections;
            const variantId = parseInt(id, 10);

            let cart = await this.fetchCart();

            const giftItems = this.getGiftLineItems(cart);
            const currentGiftItem = giftItems.filter((item) => item.variant_id === variantId);
            const otherGiftItems = giftItems.filter((item) => item.variant_id !== variantId);
            const maxOtherGiftItems = add ? this.options.max_gifts - 1 : this.options.max_gifts;

            const updateItems = {};

            if (currentGiftItem.length >= 1) {
              updateItems[currentGiftItem[0].key] = add ? 1 : 0;
            }

            if (!add) {
              sessionStorage.setItem('gift_manually_removed', true);
            }

            otherGiftItems.forEach((item, index) => {
              if (index >= maxOtherGiftItems) {
                updateItems[item.key] = 0;
              }
            });

            const objectKeysArray = Object.keys(updateItems);

            if (objectKeysArray.length) {
              const body = JSON.stringify({
                updates: updateItems,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              });

              cart = await fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }}).then(FreeGift.parseCartJson);
              sections = cart.sections;
            }

            if (add && currentGiftItem.length === 0) {
              sessionStorage.removeItem('gift_manually_removed');

              const discountTitle = FreeGift.lastAdjustment?.add?.discountTitle
                || FreeGift.lastDiscountKitPayload?.add?.discountTitle
                || FreeGift.lastDiscountKitPayload?.discounts?.[0]?.discountTitle
                || FreeGift.lastDiscountKitPayload?.discounts?.[0]?.title
                || this.discountTitle
                || FreeGift.DEFAULT_DISCOUNT_TITLE;

              const body = JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity: 1,
                    properties: {
                      _dk_gift: discountTitle
                    }
                  }
                ],
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              });

              cart = await fetch(`${routes.cart_add_url}`, {...fetchConfig(), ...{ body }}).then(FreeGift.parseCartJson);
              sections = cart.sections;
            }

            if (objectKeysArray.length || (add && currentGiftItem.length === 0)) {
              this.refreshCartSections(sections);

              const availableVariants = this.getAvailableVariants();

              if (availableVariants.length) {
                this.renderGiftList(availableVariants);
                this.show();
                await this.syncSelectionFromCart(cart);
                this.updateHeaderState(Boolean(this.getSelectedGiftVariantId(cart)));
              }
            }

            cartItems?.disableLoading();
            this.isUpdating = false;
            FreeGift.isMutatingCart = false;
            resolve();
          } catch (error) {
            this.isUpdating = false;
            FreeGift.isMutatingCart = false;
            document.querySelector('cart-items')?.disableLoading();
            const cartErrors = document.getElementById('cart-errors');

            if (cartErrors) {
              cartErrors.textContent = error?.message || window.cartStrings?.error || 'Cart error';
            }

            reject(error);
          }
        })();
      });
    }

  }

  // Static properties (kept ES5-compatible for older parsers)
  FreeGift.lastAdjustment = null;
  FreeGift.lastDiscountKitPayload = null;
  FreeGift.listenersBound = false;
  FreeGift.isMutatingCart = false;
  FreeGift.debounceTimer = null;
  FreeGift.DEFAULT_DISCOUNT_TITLE = 'Free gift';

  window.FreeGift = FreeGift;

  customElements.define('free-gift', FreeGift);

}
